import React, { useEffect } from "react";
import { Form, Input, Select, Row, Col, Card, Button } from "antd";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";

const { Option } = Select;
const { TextArea } = Input;

const SKILL_OPTIONS = [
  "React", "Angular", "Vue.js", "Next.js", "Node.js", "Express.js", "Python", "Django", "Flask",
  "Java", "Spring Boot", "PHP", "Laravel", "JavaScript", "TypeScript", "HTML/CSS", "SAP HANA",
  "SQL", "PostgreSQL", "MongoDB", "MySQL", "Redis", "Docker", "Kubernetes", "AWS", "Azure",
  "GCP", "Git", "CI/CD", "Agile", "GraphQL", "REST API", "Elasticsearch",
];

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - 1969 }, (_, i) => CURRENT_YEAR - i);

const ProfessionalDetails = ({ initialData, onValidate }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    const values = { ...initialData };
    if (!values.projects) values.projects = [];
    if (!values.primarySkills) values.primarySkills = [];
    if (!values.secondarySkills) values.secondarySkills = [];
    form.setFieldsValue(values);
  }, [initialData, form]);

  useEffect(() => {
    onValidate(() => form.validateFields());
  }, [form, onValidate]);

  return (
    <Form form={form} layout="vertical">
      <p className="section-title">Experience & Skills</p>
      <Row gutter={16}>
        <Col span={6}>
          <Form.Item label="Years of Experience" name="experienceYears" rules={[{ required: true, message: "Required" }]}>
            <Input type="number" min={0} max={50} placeholder="Years" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Months" name="experienceMonths" rules={[{ required: true, message: "Required" }]}>
            <Input type="number" min={0} max={11} placeholder="Months" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="University" name="university">
            <Input placeholder="e.g. IIT Bombay" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Graduation Year" name="graduationYear">
            <Select placeholder="Select year" showSearch allowClear>
              {YEAR_OPTIONS.map((y) => <Option key={y} value={y}>{y}</Option>)}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label="Primary Skills (min 1, max 3 — no duplicates)" required>
        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.primarySkills !== cur.primarySkills}>
          {({ getFieldValue }) => {
            const ps = getFieldValue("primarySkills") || [];
            const [s0, s1, s2] = ps;
            const opts = (exclude) =>
              SKILL_OPTIONS.filter((sk) => !exclude.filter(Boolean).includes(sk)).map((sk) => (
                <Option key={sk} value={sk}>{sk}</Option>
              ));
            return (
              <Row gutter={12}>
                <Col span={8}>
                  <Form.Item name={["primarySkills", 0]} rules={[{ required: true, message: "At least 1 primary skill is required" }]} noStyle>
                    <Select placeholder="Primary Skill 1" showSearch optionFilterProp="children" style={{ width: "100%" }}>
                      {opts([s1, s2])}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={["primarySkills", 1]} noStyle>
                    <Select placeholder="Skill 2 (optional)" showSearch optionFilterProp="children" allowClear style={{ width: "100%" }}>
                      {opts([s0, s2])}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={["primarySkills", 2]} noStyle>
                    <Select placeholder="Skill 3 (optional)" showSearch optionFilterProp="children" allowClear style={{ width: "100%" }}>
                      {opts([s0, s1])}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            );
          }}
        </Form.Item>
      </Form.Item>

      <Form.Item label="Secondary Skills" name="secondarySkills">
        <Select mode="tags" placeholder="Select or type secondary skills" showSearch allowClear>
          {SKILL_OPTIONS.map((sk) => <Option key={sk} value={sk}>{sk}</Option>)}
        </Select>
      </Form.Item>

      <Form.Item label="Professional Summary" name="summary">
        <TextArea rows={3} placeholder="Brief professional summary highlighting key skills, domain expertise, and career highlights..." />
      </Form.Item>

      <p className="section-title" style={{ marginTop: 4 }}>Project Experience</p>
      <Form.List name="projects">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...rest }) => (
              <Card
                key={key}
                size="small"
                style={{ marginBottom: 12, background: "#fafafa", borderRadius: 10, border: "1px solid #f0f0f0" }}
                extra={
                  <MinusCircleOutlined onClick={() => remove(name)} style={{ color: "#ff4d4f", cursor: "pointer" }} />
                }
              >
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item {...rest} name={[name, "title"]} label="Project Title" rules={[{ required: true, message: "Title required" }]}>
                      <Input placeholder="e.g. E-commerce Platform" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item {...rest} name={[name, "client"]} label="Client">
                      <Input placeholder="e.g. Fortune 500 Co." />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={12}>
                  <Col span={8}>
                    <Form.Item {...rest} name={[name, "duration"]} label="Duration">
                      <Input placeholder="e.g. 6 months" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item {...rest} name={[name, "role"]} label="Your Role">
                      <Input placeholder="e.g. Lead Developer" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item {...rest} name={[name, "technologies"]} label="Technologies">
                      <Input placeholder="e.g. React, Node.js" />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item {...rest} name={[name, "description"]} label="Description" style={{ marginBottom: 0 }}>
                  <TextArea rows={2} placeholder="Describe contributions and outcomes..." />
                </Form.Item>
              </Card>
            ))}
            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={{ borderRadius: 8 }}>
              Add Project
            </Button>
          </>
        )}
      </Form.List>
    </Form>
  );
};

export default ProfessionalDetails;
